import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Sparkles, Loader2, TrendingUp, PenLine, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { nb, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useChartColors, kFmt, ChartTooltip } from "@/components/finance/chart";

export function MeetingFinancials({ meetingId }) {
  const colors = useChartColors();
  const [data, setData] = useState(null);
  const [gen, setGen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setData(null);
    setEditing(false);
    api.get(`/meetings/${meetingId}/financials`).then((r) => setData(r.data)).catch(() => setData({}));
  }, [meetingId]);

  const months = data?.months || [];
  const totals = data?.totals || {};
  const barData = months
    .filter((m) => m.inntekter != null)
    .map((m) => ({ month: m.month, Inntekter: m.inntekter, Kostnader: m.kostnader, Resultat: m.resultat }));

  const generate = async () => {
    setGen(true);
    try {
      const { data: r } = await api.post(`/meetings/${meetingId}/financials/analysis`);
      setData((d) => ({ ...d, analysis: r.analysis, analysis_at: r.analysis_at }));
      toast.success("Analyse oppdatert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGen(false);
    }
  };

  const saveAnalysis = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.put(`/meetings/${meetingId}/financials/analysis`, { analysis: draft });
      setData((d) => ({ ...d, analysis: r.analysis, analysis_at: r.analysis_at }));
      setEditing(false);
      toast.success("Analyse lagret");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-72 rounded-xl" /><Skeleton className="h-28 rounded-xl" /></div>;
  if (!data.months) return <p className="text-sm text-muted-foreground">Nøkkeltall er ikke aktivert for dette møtet.</p>;

  return (
    <div className="space-y-6" data-testid="meeting-financials">
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-headings text-base font-bold tracking-tight">Inntekter, kostnader og resultat</h3>
            <p className="text-xs text-muted-foreground">{data.period_label}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium">
            {data.source?.provider} · Demodata
          </span>
        </div>
        <div className="h-[320px] w-full" data-testid="meeting-fin-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: colors.grid }} tick={{ fill: colors.axis, fontSize: 12 }} />
              <YAxis tickFormatter={kFmt} tickLine={false} axisLine={false} tick={{ fill: colors.axis, fontSize: 12 }} width={48} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: colors.grid, opacity: 0.3 }} />
              <Bar dataKey="Inntekter" fill={colors.inntekter} radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="Kostnader" fill={colors.kostnader} radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="Resultat" fill={colors.resultat} radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3 border-t border-border/60 pt-5">
          {[
            ["INNTEKTER", totals.inntekter, colors.inntekter],
            ["KOSTNADER", totals.kostnader, colors.kostnader],
            ["RESULTAT", totals.resultat, colors.resultat],
          ].map(([l, v, c]) => (
            <div key={l} className="text-center">
              <div className="mx-auto mb-2 h-1 w-10 rounded-full" style={{ background: c }} />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</p>
              <p className="mt-1 font-headings text-lg font-extrabold tabular-nums sm:text-2xl">{v != null ? nb(v) : "–"}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-headings text-base font-bold tracking-tight">
            <TrendingUp className="h-4 w-4 text-primary" /> Analyse av tallene
          </h3>
          {!editing ? (
            <div className="flex items-center gap-2">
              {data.analysis ? (
                <Button variant="ghost" size="sm" className="gap-2" data-testid="edit-analysis-btn"
                  onClick={() => { setDraft(data.analysis || ""); setEditing(true); }}>
                  <PenLine className="h-4 w-4" /> Rediger
                </Button>
              ) : null}
              <Button variant="outline" size="sm" className="gap-2" onClick={generate} disabled={gen} data-testid="generate-analysis-btn">
                {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {data.analysis ? "Oppdater med AI" : "Generer analyse"}
              </Button>
            </div>
          ) : null}
        </div>
        {editing ? (
          <div className="mt-4">
            <Textarea data-testid="edit-analysis-textarea" value={draft} onChange={(e) => setDraft(e.target.value)} rows={8} className="resize-none bg-background" />
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="cancel-analysis-btn">Avbryt</Button>
              <Button size="sm" className="gap-2" onClick={saveAnalysis} disabled={saving} data-testid="save-analysis-btn">
                <Save className="h-4 w-4" /> {saving ? "Lagrer …" : "Lagre"}
              </Button>
            </div>
          </div>
        ) : data.analysis ? (
          <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed" data-testid="fin-analysis-text">{data.analysis}</div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">Ingen analyse ennå. Klikk «Generer analyse» for en kort AI-vurdering av periodens tall.</p>
        )}
        {data.analysis_at && !editing ? <p className="mt-3 text-xs text-muted-foreground">Sist oppdatert {formatDateTime(data.analysis_at)}</p> : null}
      </div>
    </div>
  );
}
